/**
 * ControlsModulationChart.tsx
 * 
 * Visual comparison for Appendix B showing "spiky" relay thermostat output
 * versus "smooth" load compensation modulation.
 * Demonstrates why modulating controls provide better comfort and efficiency.
 */

interface ControlsModulationChartProps {
  width?: number;
  height?: number;
}

export function ControlsModulationChart({ width = 600, height = 300 }: ControlsModulationChartProps) {
  // Chart dimensions
  const margin = { top: 40, right: 40, bottom: 50, left: 60 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Target temperature line (middle of chart)
  const targetTemp = 50; // 50% of scale

  // Relay thermostat - square wave with overshoots and undershoots
  const relayData = [
    { time: 0, temp: 45 },
    { time: 0.5, temp: 45 },
    { time: 0.5, temp: 70 },  // Sharp jump on
    { time: 1.5, temp: 70 },
    { time: 1.5, temp: 25 },  // Sharp drop off
    { time: 2.5, temp: 25 },
    { time: 2.5, temp: 72 },  // Overshoot
    { time: 3.5, temp: 72 },
    { time: 3.5, temp: 23 },  // Undershoot
    { time: 4.5, temp: 23 },
    { time: 4.5, temp: 73 },
    { time: 5.5, temp: 73 },
    { time: 5.5, temp: 22 },
    { time: 6.5, temp: 22 },
    { time: 6.5, temp: 71 },
    { time: 7.5, temp: 71 },
    { time: 7.5, temp: 24 },
    { time: 8.5, temp: 24 },
    { time: 8.5, temp: 70 },
    { time: 9.5, temp: 70 },
    { time: 9.5, temp: 25 },
    { time: 10.5, temp: 25 },
    { time: 10.5, temp: 69 },
    { time: 11.5, temp: 69 },
    { time: 11.5, temp: 45 },
    { time: 12, temp: 45 },
  ];

  // Modulating system - smooth Bezier curve tracking target
  const modulatingData = [
    { time: 0, temp: 35 },
    { time: 1, temp: 45 },
    { time: 2, temp: 49 },
    { time: 3, temp: 50 },
    { time: 4, temp: 50 },
    { time: 5, temp: 51 },
    { time: 6, temp: 50 },
    { time: 7, temp: 50 },
    { time: 8, temp: 49 },
    { time: 9, temp: 50 },
    { time: 10, temp: 50 },
    { time: 11, temp: 48 },
    { time: 12, temp: 45 },
  ];

  // Scale functions
  const xScale = (t: number) => (t / 12) * chartWidth;
  const yScale = (v: number) => chartHeight - (v / 100) * chartHeight;

  // Generate jagged path for relay (polyline style)
  const generateRelayPath = (data: typeof relayData) => {
    return data
      .map((point, i) => {
        const x = xScale(point.time);
        const y = yScale(point.temp);
        return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      })
      .join(' ');
  };

  // Generate smooth Bezier path for modulating system
  const generateModulatingPath = (data: typeof modulatingData) => {
    if (data.length < 2) return '';
    
    let path = `M ${xScale(data[0].time)} ${yScale(data[0].temp)}`;
    
    // Use quadratic Bezier curves for smooth transitions
    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1];
      const curr = data[i];
      
      const x0 = xScale(prev.time);
      const y0 = yScale(prev.temp);
      const x1 = xScale(curr.time);
      const y1 = yScale(curr.temp);
      
      // Control point midway between points
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;
      
      path += ` Q ${cx} ${cy} ${x1} ${y1}`;
    }
    
    return path;
  };

  const relayPath = generateRelayPath(relayData);
  const modulatingPath = generateModulatingPath(modulatingData);
  const targetLinePath = `M 0 ${yScale(targetTemp)} L ${chartWidth} ${yScale(targetTemp)}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="dp-controls-modulation-chart"
      role="img"
      aria-label="Controls and modulation comparison showing relay thermostat versus load compensation"
    >
      <title>
        Relay thermostat (red dashed line) causes temperature swings with frequent overshoots and undershoots.
        Load compensation (solid blue line) provides smooth, steady temperature control that tracks the target.
      </title>
      <defs>
        <style>{`
          .chart-axis { stroke: #64748b; stroke-width: 1.5; }
          .chart-grid { stroke: #e2e8f0; stroke-width: 1; stroke-dasharray: 4,4; }
          .chart-label { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; fill: #475569; }
          .chart-title { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 14px; font-weight: 600; fill: #1e293b; }
          .target-line { stroke: #10b981; stroke-width: 2; stroke-dasharray: 8,4; }
          .relay-line { stroke: #dc2626; stroke-width: 2.5; fill: none; stroke-dasharray: 5,3; }
          .modulating-line { stroke: #2563eb; stroke-width: 2.5; fill: none; }
          .annotation { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 12px; fill: #64748b; font-style: italic; }
          .legend-text { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 12px; fill: #1e293b; }
        `}</style>
      </defs>

      {/* Chart group */}
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        {/* Grid lines */}
        {[25, 50, 75].map((v) => (
          <line
            key={`grid-${v}`}
            x1={0}
            y1={yScale(v)}
            x2={chartWidth}
            y2={yScale(v)}
            className="chart-grid"
          />
        ))}

        {/* Axes */}
        <line x1={0} y1={0} x2={0} y2={chartHeight} className="chart-axis" />
        <line x1={0} y1={chartHeight} x2={chartWidth} y2={chartHeight} className="chart-axis" />

        {/* Y-axis labels */}
        <text x={-10} y={yScale(75)} textAnchor="end" alignmentBaseline="middle" className="chart-label">
          Hot
        </text>
        <text x={-10} y={yScale(50)} textAnchor="end" alignmentBaseline="middle" className="chart-label">
          Target
        </text>
        <text x={-10} y={yScale(25)} textAnchor="end" alignmentBaseline="middle" className="chart-label">
          Cool
        </text>

        {/* Y-axis title */}
        <text
          x={-45}
          y={chartHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90, -45, ${chartHeight / 2})`}
          className="chart-title"
        >
          Temperature
        </text>

        {/* X-axis labels */}
        <text x={0} y={chartHeight + 25} textAnchor="start" className="chart-label">
          0h
        </text>
        <text x={chartWidth / 2} y={chartHeight + 25} textAnchor="middle" className="chart-label">
          6h
        </text>
        <text x={chartWidth} y={chartHeight + 25} textAnchor="end" className="chart-label">
          12h
        </text>

        {/* X-axis title */}
        <text x={chartWidth / 2} y={chartHeight + 40} textAnchor="middle" className="chart-title">
          Time
        </text>

        {/* Target temperature line */}
        <path d={targetLinePath} className="target-line" />

        {/* Relay thermostat line (behind, so drawn first) */}
        <path d={relayPath} className="relay-line" />

        {/* Modulating system line (on top) */}
        <path d={modulatingPath} className="modulating-line" />

        {/* Annotation */}
        <text x={chartWidth / 2} y={-20} textAnchor="middle" className="annotation">
          Smooth modulation vs. spiky relay control
        </text>
      </g>

      {/* Legend */}
      <g transform={`translate(${margin.left}, ${height - 20})`}>
        {/* Target line legend */}
        <line x1={0} y1={0} x2={30} y2={0} className="target-line" />
        <text x={35} y={0} alignmentBaseline="middle" className="legend-text">
          Target temperature
        </text>

        {/* Modulating system legend */}
        <line x1={200} y1={0} x2={230} y2={0} className="modulating-line" />
        <text x={235} y={0} alignmentBaseline="middle" className="legend-text">
          Load compensation (smooth)
        </text>

        {/* Relay system legend */}
        <line x1={chartWidth - 200} y1={0} x2={chartWidth - 170} y2={0} className="relay-line" />
        <text x={chartWidth - 165} y={0} alignmentBaseline="middle" className="legend-text">
          Relay thermostat (spiky)
        </text>
      </g>
    </svg>
  );
}
